import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "../components/UserBadge";
import { fetchServerStatus } from "../components/ServerStatusWidget";
import {
  Crown, Trophy, Users, Shield, Target, Zap, Star, Server,
  ArrowRight, Flame, Crosshair, MessageSquare, ChevronRight,
  Activity, Award, Swords, Radio, Copy, Check, Play,
  Gamepad2, ShieldCheck, UserPlus, Headphones, RefreshCw, Lock, Map
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import EsportsTournamentBracket from "../components/EsportsTournamentBracket";
import EsportsCommunityFeed from "../components/EsportsCommunityFeed";
import ProfileSidebar from "../components/ProfileSidebar";

const DEFAULT_SERVERS = [
  {
    id: "srv_1", address: "209.38.247.243:27015",
    name: "OFFICIAL.INDUNGI.PRO - 2026 NEW MOD", game_type: "cs16",
    dns: "official.indungi.pro", tags: ["Classic", "Ranked", "VIP"],
    desc: "Serverul oficial al comunității. Runde clasice, statistici și acces VIP premium.",
  },
  {
    id: "srv_2", address: "209.38.247.243:27016",
    name: "DOPE.INDUNGI.PRO - ROMANIAN LEGENDS", game_type: "cs16",
    dns: "dope.indungi.pro", tags: ["DOPE", "Ranked", "VIP"],
    desc: "Romanian Legends — server de competiție cu jucători activi și hărți clasice.",
  },
  {
    id: "srv_3", address: "209.38.247.243:27017",
    name: "MIX.INDUNGI.PRO - OFFICIAL MIX SERVER", game_type: "mix",
    dns: "mix.indungi.pro", tags: ["MIX", "5v5", "Competitiv"],
    desc: "Server oficial de mix 5v5. Organizare rapidă, echipe echilibrate, voice chat.",
  },
];

const MAP_IMAGES = {
  de_dust2: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_inferno: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  de_nuke: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_mirage: "https://image.gametracker.com/images/maps/160x120/cs/de_mirage.jpg",
  de_train: "https://image.gametracker.com/images/maps/160x120/cs/de_train.jpg",
  de_cbble: "https://image.gametracker.com/images/maps/160x120/cs/de_cbble.jpg",
  cs_office: "https://image.gametracker.com/images/maps/160x120/cs/cs_office.jpg",
};

const GAME_ICONS = {
  cs16: "https://1shot2kill.pl/uploads/monthly_2026_03/16_cs.webp.7bb969a18166855fda8b88509e32c674.webp",
  mix: "https://1shot2kill.pl/uploads/monthly_2026_03/ikonka3.webp.61be5e01f5474ae721b2f546154d3a5f.webp",
  cs2: "https://1shot2kill.pl/uploads/monthly_2026_03/2_cs.webp.cdc7c2c51eadcf0df28c4aa7d90c851d.webp",
};

function getMapImg(map = "") {
  const m = map.toLowerCase();
  for (const [k, v] of Object.entries(MAP_IMAGES)) { if (m.includes(k.split("_")[1])) return v; }
  return `https://image.gametracker.com/images/maps/160x120/cs/${m}.jpg`;
}

function AnimatedCounter({ target, suffix = "+" }) {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!target || started.current) return;
    started.current = true;
    let cur = 0;
    const step = Math.max(1, Math.ceil(target / 50));
    const t = setInterval(() => {
      cur = Math.min(cur + step, target);
      setCount(cur);
      if (cur >= target) clearInterval(t);
    }, 25);
    return () => clearInterval(t);
  }, [target]);
  return <>{count.toLocaleString()}{suffix}</>;
}

/* ─── Live Server Spotlight Card ─── */
function ServerSpotlightCard({ server }) {
  const [copied, setCopied] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["serverStatus", server.address],
    queryFn: () => fetchServerStatus(server.address),
    staleTime: 0,
    refetchInterval: 5000,
  });

  const isOnline = data?.online === true;
  const players = data?.players ?? 0;
  const maxPlayers = data?.max_players ?? 32;
  const currentMap = data?.map || "de_dust2";
  const fillPct = maxPlayers > 0 ? Math.round((players / maxPlayers) * 100) : 0;
  const gameType = server.game_type || "cs16";

  const handleConnect = () => { window.location.href = `steam://connect/${server.address}`; };
  const handleCopy = () => {
    navigator.clipboard.writeText(server.address);
    setCopied(true);
    toast.success(`Copiat: ${server.address}`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-xl border border-[#161f30] bg-[#080c14] hover:border-[var(--tf-primary)]/50 transition-all duration-300 shadow-xl">
      {/* Card glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-[var(--tf-primary)]/[0.04] to-transparent pointer-events-none" />

      {/* Cover image */}
      <div className="relative min-h-[160px] overflow-hidden">
        <img
          src={getMapImg(currentMap)}
          alt={currentMap}
          onError={e => { e.target.onerror = null; e.target.src = ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"); }}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080c14] via-[#080c14]/60 to-transparent" />

        {/* Top row: icon + status */}
        <div className="relative z-10 flex items-start justify-between p-3.5">
          <div className="w-12 h-12 rounded-lg border border-[var(--tf-primary)]/40 bg-[var(--tf-primary)]/10 flex items-center justify-center shadow-md">
            <img src={GAME_ICONS[gameType] || GAME_ICONS.cs16} alt={gameType} className="w-7 h-7 object-contain" />
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
            isLoading ? "border-zinc-700 bg-zinc-900 text-zinc-500" :
            isOnline ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" :
            "border-red-500/40 bg-red-500/10 text-red-400"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isLoading ? "bg-zinc-600" : isOnline ? "bg-emerald-400 shadow-[0_0_6px_#34d399]" : "bg-red-500"} ${isOnline && !isLoading ? "animate-pulse" : ""}`} />
            {isLoading ? "Se verifică" : isOnline ? "LIVE" : "OFFLINE"}
          </span>
        </div>

        {/* Bottom: server name */}
        <div className="relative z-10 px-3.5 pb-3 mt-4">
          <p className="text-[9px] font-black uppercase tracking-widest text-[var(--tf-primary)] mb-0.5 notranslate" translate="no">INDUNGI.PRO</p>
          <h3 className="font-chakra text-lg font-black uppercase leading-tight text-white truncate">
            {server.name.split(" - ")[1] || server.name.split(" - ")[0]}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-3.5 gap-3">
        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-[#141b2a] bg-[#05070d] p-2.5">
            <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1">
              <Users className="w-3 h-3 text-[var(--tf-primary)]" /> Jucători
            </div>
            <div className="font-chakra text-base font-black text-white">
              {isLoading ? "—" : <><span className={players > 0 ? "text-emerald-400" : ""}>{players}</span><span className="text-zinc-500"> / {maxPlayers}</span></>}
            </div>
          </div>
          <div className="rounded-lg border border-[#141b2a] bg-[#05070d] p-2.5">
            <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1">
              <Map className="w-3 h-3 text-[var(--tf-primary)]" /> Hartă
            </div>
            <div className="font-chakra text-xs font-black text-white uppercase truncate">
              {isLoading ? "—" : currentMap}
            </div>
          </div>
        </div>

        {/* Player bar */}
        <div>
          <div className="flex justify-between text-[9px] font-mono text-zinc-400 mb-1">
            <span>Ocupare server</span><span>{fillPct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${fillPct}%`,
                background: fillPct > 75 ? "#ef4444" : fillPct > 40 ? "#f97316" : "#34d399"
              }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2 mt-auto pt-1">
          <button
            onClick={handleConnect}
            disabled={!isOnline}
            className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-chakra font-black uppercase tracking-wider transition-all ${
              isOnline
                ? "bg-[var(--tf-primary)] hover:bg-[#ff6b2b] text-black shadow-md"
                : "bg-white/[0.04] border border-white/8 text-zinc-600 cursor-not-allowed"
            }`}
          >
            {isOnline ? <Play className="w-3.5 h-3.5 fill-current" /> : <Lock className="w-3.5 h-3.5" />}
            {isOnline ? "CONNECT" : "Offline"}
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-white/10 bg-[#05070d] hover:bg-white/[0.05] text-xs font-chakra font-black uppercase tracking-wider text-white transition-all"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-zinc-400" />}
            {copied ? "COPIAT" : "IP"}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Landing() {
  const { user } = useAuth();

  const { data: statsData } = useQuery({ queryKey: ["siteStats"], queryFn: async () => (await api.get("/stats")).data, staleTime: 60000 });
  const { data: topUsersData } = useQuery({ queryKey: ["topUsers"], queryFn: async () => (await api.get("/users/top?limit=3")).data, staleTime: 60000 });
  const { data: serversData } = useQuery({ queryKey: ["serversList"], queryFn: async () => (await api.get("/servers")).data, staleTime: 60000 });

  const stats = statsData || { users: 120, topics: 95, posts: 340, online_now: 18 };
  const topUsers = topUsersData || [];
  const servers = serversData && serversData.length > 0 ? serversData : DEFAULT_SERVERS;

  return (
    <div className="space-y-8 pb-12">
      {/* ─── 1. HERO ESPORTS ARENA ─── */}
      <section className="relative rounded-2xl overflow-hidden border border-[#161f30] bg-[#070a10] shadow-2xl p-6 sm:p-10 lg:p-12">
        {/* Subtle Neon Backdrop */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_top_right,rgba(0,255,136,0.12),transparent_70%)] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,79,0,0.1),transparent_70%)] pointer-events-none" />

        <div className="relative z-10 max-w-4xl space-y-5">
          {/* Tagline */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono font-black uppercase tracking-widest">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>INDUNGI // PLAYZEK ESPORTS HUB · EST. 2010</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-chakra font-black uppercase text-white tracking-tight leading-none">
            UNIFICĂM COMUNITATEA CU <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff88] via-[#00c3ff] to-[var(--tf-primary)]">ESPORTS-UL MODERN</span>
          </h1>

          <p className="text-zinc-300 text-sm sm:text-base font-sans max-w-2xl leading-relaxed">
            Platforma completă de Counter-Strike pentru România. Servere live 128-tick & 1000fps, turnee 5v5 cu fonduri de premii, sistem avansat de ranking, clan wars și comunitate activă.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Link
              to="/servere"
              className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#00ff88] to-[#00c3ff] hover:brightness-110 text-black font-chakra font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all shadow-[0_0_25px_rgba(0,255,136,0.3)]"
            >
              <Swords className="w-4 h-4" />
              <span>SERVERE LIVE (CS2 & CS 1.6)</span>
            </Link>

            <Link
              to="/turnee"
              className="px-6 py-3.5 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white font-chakra font-black text-xs uppercase tracking-wider flex items-center gap-2 transition-all"
            >
              <Trophy className="w-4 h-4 text-amber-400" />
              <span>TURNEE & BRACKET</span>
            </Link>

            <Link
              to="/forums"
              className="px-6 py-3.5 rounded-xl bg-white/[0.02] hover:bg-white/[0.06] border border-white/5 text-zinc-300 hover:text-white font-chakra font-bold text-xs uppercase tracking-wider transition-all"
            >
              <span>FORUM</span>
            </Link>
          </div>

          {/* Metric Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-6 border-t border-[#141b2a]">
            <div className="bg-[#05070d] border border-[#141b2a] p-3 rounded-lg">
              <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider">Operatori</div>
              <div className="text-xl font-chakra font-black text-white">{stats.users || 120}+</div>
            </div>
            <div className="bg-[#05070d] border border-[#141b2a] p-3 rounded-lg">
              <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider">Jucători Online</div>
              <div className="text-xl font-chakra font-black text-emerald-400">{stats.online_now || 18} LIVE</div>
            </div>
            <div className="bg-[#05070d] border border-[#141b2a] p-3 rounded-lg">
              <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider">Servere Gaming</div>
              <div className="text-xl font-chakra font-black text-cyan-400">{servers.length} Active</div>
            </div>
            <div className="bg-[#05070d] border border-[#141b2a] p-3 rounded-lg">
              <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider">Anti-Cheat</div>
              <div className="text-xl font-chakra font-black text-amber-400">100% PROTECTED</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── 2. LIVE SERVERS SPOTLIGHT MATRIX ─── */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-chakra font-black uppercase text-white tracking-wide flex items-center gap-2">
              <Server className="w-5 h-5 text-[var(--tf-primary)]" />
              <span>SERVERE LIVE INDUNGI // REAL-TIME NETWORK</span>
            </h2>
            <p className="text-xs font-mono text-zinc-400 mt-0.5">
              Conectare directă prin protocol Steam · Tickrate competitiv · Protecție Anti-DDoS
            </p>
          </div>
          <Link
            to="/servere"
            className="text-xs font-chakra font-bold uppercase text-[var(--tf-primary)] hover:underline flex items-center gap-1 shrink-0"
          >
            <span>TOATE SERVERELE</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {servers.slice(0, 3).map((s) => (
            <ServerSpotlightCard key={s.id || s.address} server={s} />
          ))}
        </div>
      </section>

      {/* ─── 3. TOURNAMENTS & BRACKETS ─── */}
      <section className="space-y-4">
        <EsportsTournamentBracket />
      </section>

      {/* ─── 4. MAIN FEED & ESPORTS HIGHLIGHTS ─── */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left 8 Columns: Modular Esports Community Feed */}
        <div className="lg:col-span-8 space-y-6">
          <EsportsCommunityFeed />
        </div>

        {/* Right 4 Columns: Esports Hub & Community Highlights */}
        <div className="lg:col-span-4 space-y-6">
          {/* Hall of Fame / MVP Podium */}
          <div className="bg-[#080c14] border border-[#161f30] rounded-xl p-4 shadow-xl space-y-3">
            <div className="flex items-center justify-between border-b border-[#141b2a] pb-2.5">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" />
                <h4 className="font-chakra font-black text-xs uppercase tracking-widest text-white">
                  TOP OPERATORI // LUNAR
                </h4>
              </div>
              <Link to="/leaderboard" className="text-[10px] font-mono text-zinc-400 hover:text-white uppercase">
                Vezi Top
              </Link>
            </div>

            <div className="space-y-2">
              {topUsers.slice(0, 3).map((u, idx) => (
                <div
                  key={u.id || idx}
                  className="flex items-center justify-between p-2.5 rounded-lg bg-[#05070d] border border-[#141b2a] hover:border-amber-500/30 transition-all"
                >
                  <div className="flex items-center gap-2.5">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center font-chakra font-black text-xs ${
                      idx === 0 ? "bg-amber-400 text-black shadow-[0_0_10px_#f59e0b]" :
                      idx === 1 ? "bg-zinc-300 text-black" :
                      "bg-amber-700 text-white"
                    }`}>
                      #{idx + 1}
                    </span>
                    <Avatar user={u} size={32} />
                    <Username user={u} className="text-xs font-chakra font-bold text-white" />
                  </div>
                  <div className="text-xs font-chakra font-black text-amber-400">
                    {u.points || 0} PTS
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Access Tiles */}
          <div className="bg-[#080c14] border border-[#161f30] rounded-xl p-4 shadow-xl space-y-3">
            <div className="flex items-center gap-2 border-b border-[#141b2a] pb-2.5">
              <Shield className="w-4 h-4 text-[var(--tf-primary)]" />
              <h4 className="font-chakra font-black text-xs uppercase tracking-widest text-white">
                RESURSE & COMPETIȚIE
              </h4>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Link
                to="/clans"
                className="p-3 rounded-lg bg-[#05070d] hover:bg-[#0c121e] border border-[#141b2a] hover:border-[var(--tf-primary)]/40 transition-all group"
              >
                <Swords className="w-4 h-4 text-[var(--tf-primary)] mb-1" />
                <div className="text-xs font-chakra font-black uppercase text-white group-hover:text-[var(--tf-primary)]">
                  Clan Wars
                </div>
                <div className="text-[9px] font-mono text-zinc-500 mt-0.5">
                  Ligi & Echipe
                </div>
              </Link>

              <Link
                to="/resurse"
                className="p-3 rounded-lg bg-[#05070d] hover:bg-[#0c121e] border border-[#141b2a] hover:border-cyan-500/40 transition-all group"
              >
                <Crosshair className="w-4 h-4 text-cyan-400 mb-1" />
                <div className="text-xs font-chakra font-black uppercase text-white group-hover:text-cyan-400">
                  Resurse CS
                </div>
                <div className="text-[9px] font-mono text-zinc-500 mt-0.5">
                  CFG & Hărți
                </div>
              </Link>
            </div>
          </div>

          {/* Quick Discord & Voice Hub */}
          <div className="bg-gradient-to-br from-[#5865F2]/20 via-[#080c14] to-[#080c14] border border-[#5865F2]/30 rounded-xl p-4 shadow-xl space-y-3">
            <div className="flex items-center gap-2">
              <Headphones className="w-5 h-5 text-[#5865F2]" />
              <h4 className="font-chakra font-black text-xs uppercase tracking-widest text-white">
                DISCORD VOICE HUB
              </h4>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed font-sans">
              Canale dedicate de mix 5v5, asistență 24/7 de la staff și anunțuri instant de turnee.
            </p>
            <a
              href="https://discord.gg/CrGC77X8W"
              target="_blank"
              rel="noreferrer"
              className="block w-full py-2 text-center rounded-lg bg-[#5865F2] hover:bg-[#4752c4] text-white font-chakra font-black text-xs uppercase tracking-wider transition-all shadow-md"
            >
              CONECTEAZĂ-TE PE DISCORD
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
